import React, {useEffect} from "react";
import {ProductionType} from "../config/js/production-type";
import {callExternalApi} from "./external-api.service";
import {AppContext} from "../app-context";
import {Auth0Context} from "@auth0/auth0-react";

export function useMongoDBOrdersService() {
    const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;
    const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;


    const {currentOrder, setCurrentOrder} = React.useContext(AppContext);
    const {user, getAccessTokenSilently} = React.useContext(Auth0Context);
    const [accessToken, setAccessToken] = React.useState(null);

    useEffect(() => {
        const getToken = async () => {
            try {
                const accessToken = await getAccessTokenSilently();
                setAccessToken(accessToken);
            } catch (e) {
                console.log(e.message);
            }
        };
        getToken().then();
    }, [auth0Domain, getAccessTokenSilently, user]);

    const [orders, setOrders] = React.useState([]);
    const [sort, setSort] = React.useState("deliveryDate:1")
    const [filterNumber, setFilterNumber] = React.useState("")
    const [filterStatut, setFilterStatut] = React.useState("ALL")
    const [filterDate, setFilterDate] = React.useState("ALL")
    const [filterType, setFilterType] = React.useState("ALL")

    // Fetch all orders on load and whenever our collection changes (e.g. if the current user changes)
    React.useEffect(() => {
        let pipeline

        let filterNumberClause = []
        let filterStatutClause = []
        let filterDateClause = []
        let filterTypeClause = []
        let $match
        let $matchPostAgg


        if (currentOrder === undefined || currentOrder == null) {
            setCurrentOrder({_id: 0});
        }

        if (currentOrder._id === 0) {
            if (filterNumber != null && filterNumber.length >= 3) {
                filterNumberClause = [{_id: { $regex: '(?i)^.*' + filterNumber + '.*'}}]
            }
        } else {
            filterNumberClause = [{_id: currentOrder._id}]
        }

        if (filterStatut === "ALL") {
            filterStatutClause = [{statut: {$ne: 'ARCHIVE'}}]
        }
        if (filterStatut != null && filterStatut !== "ALL") {
            filterStatutClause = [{statut: filterStatut}]
        }

        if (filterDate != null && filterDate !== "ALL") {
            let dateParts = filterDate.split("/")
            //TODO: gestion de dates coté back
            let deliveryDate = (new Date(Date.UTC(+dateParts[2], dateParts[1] - 1, +dateParts[0]))).toISOString()
            filterDateClause = [{deliveryDate: deliveryDate}]
        }

        if (filterType != null && filterType !== "ALL") {
            if (filterType.endsWith("-EC")) {
                if (filterType.startsWith("PVC-")) {
                    filterTypeClause = [{"parts.productionType": "PVC"}]
                    $matchPostAgg = {$expr: {$ne: ['$nbPVC', '$nbCompletePVC']}}
                }
                if (filterType.startsWith("ALU-")) {
                    filterTypeClause = [{"parts.productionType": "ALU"}]
                    $matchPostAgg = {$expr: {$ne: ['$nbALU', '$nbCompleteALU']}}
                }
            } else {
                if (filterType.indexOf(',') > 0) {
                    filterTypeClause = [{"parts.productionType": {$in: filterType.split(',')}}]
                } else {
                    filterTypeClause = [{"parts.productionType": filterType}]
                }
                $matchPostAgg = {}
            }
        } else {
            $matchPostAgg = {}
        }

        let concatClause = filterNumberClause.concat(filterStatutClause).concat(filterDateClause).concat(filterTypeClause)
        if (concatClause != null && concatClause.length >= 1) {
            $match = {$and: concatClause}
        } else {
            $match = {}
        }

        let $sort
        switch (sort) {
            case "_id:1":
                $sort = {_id: 1}
                break;
            case "_id:-1":
                $sort = {_id: -1}
                break;
            case "deliveryDate:1":
                $sort = {deliveryDate: 1, _id: 1}
                break;
            case "deliveryDate:-1":
                $sort = {deliveryDate: -1, _id: 1}
                break;
            default:
                $sort = {deliveryDate: 1, _id: 1}
                break;
        }

        pipeline = [
            {$match},
            {$unwind: "$parts"},
            {
                $group: {
                    _id: "$_id",
                    firstProductionDateTS: {"$min": "$parts.productionDate"},
                    statut: {$first: "$statut"},
                    deliveryDate: {$first: "$deliveryDate"},
                    parts: {$push: "$parts"}
                }
            },
            {
                $addFields: {
                    firstProductionDate: {$toDate: "$firstProductionDateTS"},
                    nbPVC: {
                        "$size": {
                            "$filter": {
                                "input": "$parts",
                                "cond": {"$and": [{"$eq": ["$$this.productionType", "PVC"]}]}
                            }
                        }
                    },
                    nbCompletePVC: {
                        "$size": {
                            "$filter": {
                                "input": "$parts",
                                "cond": {"$and": [{"$eq": ["$$this.productionType", "PVC"]}, {"$eq": ["$$this.completeFab", true]}]}
                            }
                        }
                    },
                    nbVR: {
                        "$size": {
                            "$filter": {
                                "input": "$parts",
                                "cond": {"$and": [{"$eq": ["$$this.productionType", "VR"]}]}
                            }
                        }
                    },
                    nbCompleteVR: {
                        "$size": {
                            "$filter": {
                                "input": "$parts",
                                "cond": {"$and": [{"$eq": ["$$this.productionType", "VR"]}, {"$eq": ["$$this.completeFab", true]}]}
                            }
                        }
                    },
                    nbNEG: {
                        "$size": {
                            "$filter": {
                                "input": "$parts",
                                "cond": {"$and": [{"$eq": ["$$this.productionType", "NEG"]}]}
                            }
                        }
                    },
                    nbCompleteNEG: {
                        "$size": {
                            "$filter": {
                                "input": "$parts",
                                "cond": {"$and": [{"$eq": ["$$this.productionType", "NEG"]}, {"$eq": ["$$this.completeFab", true]}]}
                            }
                        }
                    },
                    nbBP: {
                        "$size": {
                            "$filter": {
                                "input": "$parts",
                                "cond": {"$and": [{"$eq": ["$$this.productionType", "BP"]}]}
                            }
                        }
                    },
                    nbCompleteBP: {
                        "$size": {
                            "$filter": {
                                "input": "$parts",
                                "cond": {"$and": [{"$eq": ["$$this.productionType", "BP"]}, {"$eq": ["$$this.completeFab", true]}]}
                            }
                        }
                    },
                    nbALU: {
                        "$size": {
                            "$filter": {
                                "input": "$parts",
                                "cond": {"$and": [{"$eq": ["$$this.productionType", "ALU"]}]}
                            }
                        }
                    },
                    nbCompleteALU: {
                        "$size": {
                            "$filter": {
                                "input": "$parts",
                                "cond": {"$and": [{"$eq": ["$$this.productionType", "ALU"]}, {"$eq": ["$$this.completeFab", true]}]}
                            }
                        }
                    }
                }
            },
            {$match: $matchPostAgg},
            {$sort},
            {$limit: 50}
        ]

        //console.log(pipeline)
        //console.log(JSON.stringify(pipeline))

        const config = {
            url: `${apiServerUrl}/api/orders/aggregate`,
            method: "GET",
            params: {"pipeline": JSON.stringify(pipeline)},
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        if(accessToken != null) {
            //console.log(JSON.stringify(config))
            callExternalApi({config}).then((result) => {
                if (result.error != null) console.log(result.error);
                if (result.data !== undefined && result.data != null) {
                    setOrders(Array.from(result.data));
                }
            });
        } else {
            console.log("Access Token not found!")
        }

    }, [sort, filterNumber, filterStatut, filterType, filterDate, currentOrder, apiServerUrl, user, accessToken, setCurrentOrder]);


    // Given a draft order, format it and then insert it
    const createOrder = async (valuesObj) => {
        let values = Object.entries(valuesObj);
        const dateParts = valuesObj.deliveryDate.split("/")
        const deliveryDate = new Date(Date.UTC(+dateParts[2], dateParts[1] - 1, +dateParts[0]))
        const order = {
            "_id": valuesObj.number,
            //TODO: gestion de dates coté back
            "deliveryDate": deliveryDate,
            "statut": 'PLAN',
            "parts": []
        }
        let i = 0;
        for (let j = 0; j < values.length; j++) {
            let currentValue = values[j];
            let key = currentValue[0];
            let value = currentValue[1]
            if (value === true && ProductionType.get(key) !== undefined) {
                //console.log(ProductionType.get(key).type)
                //console.log(ProductionType.get(key).productionType)
                order.parts.push(
                    {
                        "_id": i++,
                        "type": ProductionType.get(key).type,
                        "productionType": ProductionType.get(key).productionType,
                        //TODO: gestion de dates coté back
                        "productionDate": (new Date(deliveryDate - ProductionType.get(key).productionDateDayMinus * 24 * 60 * 60 * 1000)).toISOString(),
                        "comment": "",
                        "nbFab": 0,
                        "rafFab": 0,
                        "signFab": "",
                        "zoneFab": "",
                        "completeFab": false,
                        "nbLog": 0,
                        "rafLog": 0,
                        "signLog": "",
                        "zoneLog": "",
                        "completeLog": false
                    }
                )
            }
        }

        const config = {
            url: `${apiServerUrl}/api/orders/insertOne`,
            method: "POST",
            data: {"data": JSON.stringify(order)},
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});

        await setCurrentOrder(order)
    };

    const createOrderLines = async (order, valuesObj) => {
        let values = Object.entries(valuesObj);

        let maxPartId = Math.max(...order.parts.map(o => o._id))

        let i = maxPartId + 1;
        for (let j = 0; j < values.length; j++) {
            let currentValue = values[j];
            let key = currentValue[0];
            let value = currentValue[1]
            if (value === true && ProductionType.get(key) !== undefined) {
                let updateFilter = {"_id": order._id}
                let updateCommand = {
                    $push: {
                        parts: {
                            "_id": i++,
                            "type": ProductionType.get(key).type,
                            "productionType": ProductionType.get(key).productionType,
                            "productionDate": (new Date(order.deliveryDate - ProductionType.get(key).productionDateDayMinus * 24 * 60 * 60 * 1000)).toISOString(),
                            "comment": "",
                            "nbFab": 0,
                            "rafFab": 0,
                            "signFab": "",
                            "zoneFab": "",
                            "completeFab": false,
                            "nbLog": 0,
                            "rafLog": 0,
                            "signLog": "",
                            "zoneLog": "",
                            "completeLog": false
                        }
                    }
                }
                const config = {
                    url: `${apiServerUrl}/api/orders/insertOne`,
                    method: "POST",
                    data: {"filter": JSON.stringify(updateFilter), "command": JSON.stringify(updateCommand)},
                    headers: {
                        "content-type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                };
                const {data, error} = await callExternalApi({config});
            }
        }
    };

    const updateOrder = async (order) => {
        const config = {
            url: `${apiServerUrl}/api/orders/findOneAndReplace`,
            method: "POST",
            data: {"filter": JSON.stringify({"_id": order._id}), "data": JSON.stringify(order)},
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    };

    const updateComment = async (order, val, partId) => {
        if (order.comment === val) return
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {"filter": JSON.stringify({"_id": order._id, "parts._id": partId}), "command": JSON.stringify({$set: {"parts.$.comment": val}})},
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const updateZoneFab = async (order, val, partId) => {
        if (order.zoneFab === val) return
        if (order.comment === val) return
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {
                "filter": JSON.stringify({"_id": order._id, "parts._id": partId}),
                "command": JSON.stringify({$set: {"parts.$.zoneFab": val, "parts.$.signFab": user.defaultInitials}})
            },
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const updateZoneLog = async (order, val, partId) => {
        if (order.zoneLog === val) return
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {
                "filter": JSON.stringify({"_id": order._id, "parts._id": partId}),
                "command": JSON.stringify({$set: {"parts.$.zoneLog": val, "parts.$.signLog": user.defaultInitials}})
            },
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const updateCompleteFab = async (order, val, partId) => {
        if (order.completeFab === val) return
        if (order.zoneLog === val) return
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {
                "filter": JSON.stringify({"_id": order._id, "parts._id": partId}),
                "command": JSON.stringify({$set: {"parts.$.completeFab": val, "parts.$.signFab": user.defaultInitials}})
            },
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const updateCompleteLog = async (order, val, partId) => {
        if (order.completeLog === val) return
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {
                "filter": JSON.stringify({"_id": order._id, "parts._id": partId}),
                "command": JSON.stringify({$set: {"parts.$.completeLog": val, "parts.$.signLog": user.defaultInitials}})
            },
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const updateNbLog = async (order, val, partId) => {
        if (order.nbLog === val) return
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {
                "filter": JSON.stringify({"_id": order._id, "parts._id": partId}),
                "command": JSON.stringify({$set: {"parts.$.nbLog": parseInt(val, 10), "parts.$.signLog": user.defaultInitials}})
            },
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const updateNbFab = async (order, val, partId) => {
        if (order.nbFab === val) return
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {
                "filter": JSON.stringify({"_id": order._id, "parts._id": partId}),
                "command": JSON.stringify({$set: {"parts.$.nbFab": parseInt(val, 10), "parts.$.signFab": user.defaultInitials}})
            },
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const updateRafLog = async (order, val, partId) => {
        if (order.rafLog === val) return
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {
                "filter": JSON.stringify({"_id": order._id, "parts._id": partId}),
                "command": JSON.stringify({$set: {"parts.$.rafLog": parseInt(val, 10), "parts.$.signLog": user.defaultInitials}})
            },
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const updateRafFab = async (order, val, partId) => {
        if (order.rafFab === val) return
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {
                "filter": JSON.stringify({"_id": order._id, "parts._id": partId}),
                "command": JSON.stringify({$set: {"parts.$.rafFab": parseInt(val, 10), "parts.$.signFab": user.defaultInitials}})
            },
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const updateStatut = async (order, val) => {
        if (order.statut === val) return
        let value
        if (val.target) {
            value = val.target.value
        } else {
            value = val
        }
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {"filter": JSON.stringify({"_id": order._id}), "command": JSON.stringify({$set: {"statut": value}})},
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const updateDeliveryDate = async (order, val) => {
        const value = val.target.value
        const dateParts = value.split("/")
        const deliveryDate = new Date(Date.UTC(dateParts[2], dateParts[1] - 1, +dateParts[0]))

        order.deliveryDate = deliveryDate
        const config = {
            url: `${apiServerUrl}/api/orders/updateMany`,
            method: "POST",
            data: {
                "filter": JSON.stringify({"_id": order._id}),
                "command": JSON.stringify({
                    $set: {
                        //TODO: gestion de dates coté back
                        "deliveryDate": deliveryDate.toISOString(),
                        "parts.$[no].productionDate": (new Date(deliveryDate - 2 * 24 * 60 * 60 * 1000)).toISOString(),
                        "parts.$[vr].productionDate": (new Date(deliveryDate - 3 * 24 * 60 * 60 * 1000)).toISOString()
                    }
                }),
                "condition": JSON.stringify({
                    arrayFilters: [
                        {"no.productionType": {$ne: "VR"}},
                        {"vr.productionType": {$eq: "VR"}}
                    ]
                })
            },
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const deleteOrder = async (order) => {
        const config = {
            url: `${apiServerUrl}/api/orders/deleteOne`,
            method: "POST",
            data: {"filter": JSON.stringify({"_id": order._id})},
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const {data, error} = await callExternalApi({config});
    }

    const deletePart = async (order, partId) => {
        const config = {
            url: `${apiServerUrl}/api/orders/updateOne`,
            method: "POST",
            data: {"filter": JSON.stringify({"_id": order._id}), "command": JSON.stringify({$pull: {parts: {_id: partId}}})},
            headers: {
                "content-type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            }
        };
        const {data, error} = await callExternalApi({config});
    }

    return {
        orders: orders,
        createOrder: createOrder,
        createOrderLines: createOrderLines,
        updateOrder: updateOrder,
        deleteOrder: deleteOrder,
        updateComment: updateComment,
        updateZoneFab: updateZoneFab,
        updateZoneLog: updateZoneLog,
        updateCompleteFab: updateCompleteFab,
        updateCompleteLog: updateCompleteLog,
        updateNbLog: updateNbLog,
        updateRafLog: updateRafLog,
        updateNbFab: updateNbFab,
        updateRafFab: updateRafFab,
        updateStatut: updateStatut,
        updateDeliveryDate: updateDeliveryDate,
        deletePart: deletePart,
        sort: sort,
        filterNumber: filterNumber,
        filterStatut: filterStatut,
        filterDate: filterDate,
        filterType: filterType,
        setSort: setSort,
        setFilterNumber: setFilterNumber,
        setFilterStatut: setFilterStatut,
        setFilterDate: setFilterDate,
        setFilterType: setFilterType
    };
}
